import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, FileText, Package, Receipt, User as UserIcon, AlertTriangle } from 'lucide-react';
import { reconcilePayables } from '../utils/payableReconcile';
import { computePayableReadiness } from '../utils/payableReadiness';
import { getCreatorPaymentAllocations } from '../utils/paymentCalculations';
import { formatEuro } from '../utils/currency';
import { Button } from './ui';
import { type TabItem } from './CampaignFormTabs';
import { GeneralInfoTab } from './campaign-form/GeneralInfoTab';
import { ContentTab } from './campaign-form/ContentTab';
import type { InfluencerBlockData } from './CampaignInfluencerBlock';
import type { ServiceBlockData } from './CampaignServiceBlock';
import type { CampaignWithComputed } from '../types/campaign';
import { campaignService } from '../services/campaignService';
import { clientService } from '../services/clientService';
import { influencerService } from '../services/influencerService';
import { campaignInfluencerService } from '../services/campaignInfluencerService';
import { deliverableService } from '../services/deliverableService';
import { serviceProviderService } from '../services/serviceProviderService';
import { campaignServiceService } from '../services/campaignServiceService';
import { invoiceService, netToInvoiceAmounts } from '../services/invoiceService';
import { toLocalDateOnly } from '../utils/dates';
import { invoiceLinkService, type InvoiceLink } from '../services/invoiceLinkService';
import { campaignAccessService } from '../services/campaignAccessService';
import { useAuth } from '../contexts/AuthContext';
import type { Client } from '../types/client';
import type { Influencer } from '../types/influencer';
import type { ServiceProvider } from '../types/serviceProvider';
import type { InvoiceWithPayments } from '../types/multiInfluencer';
import { useLanguage, ValidationError } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { useAgencyDefaultCommission } from '../hooks/useAgencyDefaultCommission';
import { AGENCY_DEFAULT_COMMISSION_FALLBACK } from '../services/settingsService';
import { ErrorMessage } from './ErrorMessage';
import { MohitInvoices, type InvoiceInfluencerCommission, type DeliverableItem } from './MohitInvoices';
import { InfluencerInvoices } from './InfluencerInvoices';
import type { MohitInvoice, InfluencerInvoice, CampaignExpense } from '../types/campaign';
import { parseJsonArray } from '../lib/jsonColumn';
import { reportFirstMissingField } from '../utils/requiredFields';
import { CampaignExpenses } from './CampaignExpenses';
import { useCampaignCalculations, calculateInfluencerNetPay } from '../hooks/useCampaignCalculations';
import { useStatusChangeNotification } from '../hooks/useStatusChangeNotification';
import { log } from '../lib/logger';
import { AdminOnlyToggle } from './AdminOnlyToggle';

interface CampaignFormMultiInfluencerProps {
  campaign?: CampaignWithComputed | null;
  onClose: () => void;
  onSave: (saved?: CampaignWithComputed) => void;
}

export function CampaignFormMultiInfluencer({ campaign, onClose, onSave }: CampaignFormMultiInfluencerProps) {
  const { user, isAdmin } = useAuth();
  const { t, translateError } = useLanguage();
  const { toast, showConfirm } = useModal();
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; technical: string } | null>(null);
  const agentName = user?.user_metadata?.full_name || user?.email || 'Desconhecido';

  const [formData, setFormData] = useState({
    year: campaign?.year || currentYear,
    work_code: campaign?.work_code || '',
    client: campaign?.client || '',
    brand: campaign?.brand || '',
    campaign_ref: campaign?.campaign_ref || '',
    agent_name: campaign?.agent_name || agentName,
    campaign_budget: campaign?.campaign_budget || 0,
    admin_only: (campaign as unknown as { admin_only?: boolean })?.admin_only ?? false,
  });

  const [activeTab, setActiveTab] = useState<string>('general');
  const [influencerBlocks, setInfluencerBlocks] = useState<InfluencerBlockData[]>([]);
  const [serviceBlocks, setServiceBlocks] = useState<ServiceBlockData[]>([]);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const [expandedServiceBlocks, setExpandedServiceBlocks] = useState<Set<string>>(new Set());
  const [clients, setClients] = useState<string[]>([]);
  const [allInfluencers, setAllInfluencers] = useState<Influencer[]>([]);
  const [allServiceProviders, setAllServiceProviders] = useState<ServiceProvider[]>([]);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [clientInvoices, setClientInvoices] = useState<MohitInvoice[]>([]);
  const [campaignExpenses, setCampaignExpenses] = useState<CampaignExpense[]>(
    parseJsonArray<CampaignExpense>((campaign as any)?.campaign_expenses)
  );
  const [influencerInvoices, setInfluencerInvoices] = useState<InfluencerInvoice[]>([]);
  const [clientInvoiceErrors, setClientInvoiceErrors] = useState<Set<string>>(new Set());
  const [influencerInvoiceErrors, setInfluencerInvoiceErrors] = useState<Set<string>>(new Set());
  const [clientTotalMismatch, setClientTotalMismatch] = useState<{ expected: number; actual: number } | null>(null);
  const [influencerTotalMismatch, setInfluencerTotalMismatch] = useState<{ expected: number; actual: number } | null>(null);
  const [invoiceLinks, setInvoiceLinks] = useState<InvoiceLink[]>([]);
  const [invoiceDeliverableMap, setInvoiceDeliverableMap] = useState<Record<string, string[]>>({});
  const [initialInfluencerInvoiceIds, setInitialInfluencerInvoiceIds] = useState<Set<string>>(new Set());
  const [savedPayablesLoaded, setSavedPayablesLoaded] = useState(false);
  const [payableAdjustNote, setPayableAdjustNote] = useState<string | null>(null);
  const payablesReconciledRef = useRef(false);

  useStatusChangeNotification(campaign?.id || null, (campaign as any)?.campaign_status || null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsData, influencersData, serviceProvidersData] = await Promise.all([
          clientService.getAll(),
          influencerService.getAll(),
          serviceProviderService.getAll()
        ]);
        setClients(clientsData.map(c => c.name));
        setAllInfluencers(influencersData);
        setAllServiceProviders(serviceProvidersData);

        if (campaign) {
          const campaignInfluencers = await campaignInfluencerService.getByCampaign(campaign.id);

          const blocks: InfluencerBlockData[] = await Promise.all(
            campaignInfluencers.map(async (ci) => {
              const deliverables = await deliverableService.getByInfluencer(ci.id);

              return {
                id: ci.id,
                influencer_id: ci.influencer_id,
                influencer_name: ci.influencer_name,
                agreed_fee: ci.agreed_fee || 0,
                commission_pct: ci.commission_pct ?? 0,
                commission_flat_fee: ci.commission_flat_fee ?? 0,
                commission_type: ci.commission_type ?? 'percentage',
                notes: ci.notes || '',
                deliverables: deliverables.map(d => {
                  let content = d.title || '';
                  if (!content && (d.platform || d.type)) {
                    const parts = [d.type, d.platform].filter(Boolean);
                    content = parts.join(' ');
                  }
                  return {
                    id: d.id,
                    content,
                    date: d.due_date || '',
                    status: d.status,
                    fee: d.fee || 0,
                    commission: (d as any).commission || 0,
                  };
                }),
                existing: true
              };
            })
          );

          const unassignedDeliverables = await deliverableService.getUnassignedByCampaign(campaign.id);
          if (unassignedDeliverables.length > 0 && blocks.length > 0) {
            blocks[0].deliverables = [
              ...blocks[0].deliverables,
              ...unassignedDeliverables.map(d => {
                let content = d.title || '';
                if (!content && (d.platform || d.type)) {
                  const parts = [d.type, d.platform].filter(Boolean);
                  content = parts.join(' ');
                }
                return {
                  id: d.id,
                  content,
                  date: d.due_date || '',
                  status: d.status,
                  fee: d.fee || 0,
                  commission: (d as any).commission || 0,
                };
              })
            ];
          }

          setInfluencerBlocks(blocks);
          // Open the first creator with work still to publish; creators whose
          // deliverables are all published stay collapsed (their header
          // already shows n/n published)
          const firstOpen = blocks.find(b =>
            b.deliverables.length === 0 || b.deliverables.some(d => d.status !== 'Published')
          );
          if (firstOpen) {
            setExpandedBlocks(new Set([firstOpen.id]));
          }

          const campaignServices = await campaignServiceService.getByCampaign(campaign.id);
          const serviceBlocks: ServiceBlockData[] = campaignServices.map(cs => ({
            id: cs.id,
            service_provider_id: cs.service_provider_id,
            service_provider_name: cs.service_provider_name,
            service_type: cs.service_type,
            status: cs.status,
            agreed_fee: cs.agreed_fee || 0,
            description: cs.description || '',
            notes: cs.notes || '',
            existing: true,
            is_recurring: cs.is_recurring || false,
            billing_interval: cs.billing_interval || null,
            custom_interval_days: cs.custom_interval_days || null,
            recurring_start_date: cs.recurring_start_date || '',
            recurring_end_date: cs.recurring_end_date || '',
          }));

          setServiceBlocks(serviceBlocks);
          if (serviceBlocks.length > 0) {
            setExpandedServiceBlocks(new Set([serviceBlocks[0].id]));
          }

          // Load invoice correlation links
          try {
            const links = await invoiceLinkService.getByCampaign(campaign.id);
            setInvoiceLinks(links);
          } catch (linkErr) {
            log.warn('Failed to load invoice links:', linkErr);
          }

          // Load invoices
          const allInvoices = await invoiceService.getByCampaign(campaign.id);
          const clientInvs = allInvoices.filter(inv => inv.type === 'Client');
          const influencerInvs = allInvoices.filter(inv => inv.type === 'Influencer');

          // Convert to MohitInvoice format
          const mohitInvoices: MohitInvoice[] = clientInvs.map(inv => ({
            id: inv.id,
            status: inv.status === 'Draft' ? 'Not Issued' : inv.status === 'Paid' ? 'Paid' : inv.status === 'Canceled' ? 'Cancelled' : 'Issued',
            invoice_number: inv.number || '',
            purchase_order: '',
            issued_date: inv.issue_date || '',
            due_date: inv.due_date || '',
            amount_received: Number(inv.subtotal) || Number(inv.total) || 0,
            // paid_at is a timestamp; date inputs render blank unless given YYYY-MM-DD
            payment_received_date: inv.payments && inv.payments.length > 0 ? toLocalDateOnly(inv.payments[0].paid_at) : '',
            client_invoice_number: inv.number || '',
          }));

          // Convert to InfluencerInvoice format
          const inflInvoices: InfluencerInvoice[] = influencerInvs.map(inv => ({
            id: inv.id,
            invoice_number: inv.number || '',
            issue_date: inv.issue_date || '',
            due_date: inv.due_date || '',
            amount: Number(inv.subtotal) || Number(inv.total) || 0,
            status: inv.status === 'Draft'
              ? (inv.notes === 'STATUS: Requested' ? 'Requested' : 'Not Requested')
              : inv.status === 'Issued' ? 'Received' : inv.status === 'Paid' ? 'Paid' : 'Not Requested',
            payment_received_date: inv.payments && inv.payments.length > 0 ? toLocalDateOnly(inv.payments[0].paid_at) : '',
            source_name: inv.counterparty_name || '',
          }));

          setClientInvoices(mohitInvoices.length > 0 ? mohitInvoices : [{
            id: `temp-${crypto.randomUUID()}`,
            status: 'Not Issued',
            invoice_number: '',
            purchase_order: '',
            issued_date: '',
            due_date: '',
            amount_received: 0,
            payment_received_date: '',
            client_invoice_number: '',
          }]);

          // Rehydrate which deliverables are linked to each client invoice
          if (clientInvs.length > 0) {
            const linkMap: Record<string, string[]> = {};
            for (const inv of clientInvs) {
              try {
                const ids = await deliverableService.getSelectedIdsByInvoice(inv.id);
                if (ids.length > 0) linkMap[inv.id] = ids;
              } catch (e) {
                log.warn('Failed to load invoice deliverable links for', inv.id, e);
              }
            }
            setInvoiceDeliverableMap(linkMap);
          }

          setInfluencerInvoices(inflInvoices.length > 0 ? inflInvoices : [{
            id: `temp-${crypto.randomUUID()}`,
            invoice_number: '',
            issue_date: '',
            due_date: '',
            amount: 0,
            status: 'Not Requested',
            payment_received_date: '',
            source_name: '',
          }]);
          if (inflInvoices.length > 0) {
            setInitialInfluencerInvoiceIds(new Set(inflInvoices.map(inv => inv.id)));
            setSavedPayablesLoaded(true);
          }
        } else {
          // A new campaign starts without client invoices: they are requested later
          setClientInvoices([]);

          setInfluencerInvoices([{
            id: `temp-${crypto.randomUUID()}`,
            invoice_number: '',
            issue_date: '',
            due_date: '',
            amount: 0,
            status: 'Not Requested',
            payment_received_date: '',
            source_name: '',
          }]);
        }
      } catch (err) {
        log.error('Failed to load data:', err);
      }
      setSnapshotVersion(v => v + 1);
    };
    loadData();
  }, [campaign]);

  useEffect(() => {
    const loadClientDetails = async () => {
      if (formData.client) {
        const client = await clientService.getByName(formData.client);
        setClientDetails(client);
      } else {
        setClientDetails(null);
      }
    };
    loadClientDetails();
  }, [formData.client]);

  useEffect(() => {
    if (!campaign && formData.client && formData.year) {
      campaignService.generateNextWorkCode(formData.year).then(code => {
        setFormData(prev => ({ ...prev, work_code: code }));
      }).catch(err => {
        log.error('Failed to generate work code:', err);
      });
    }
  }, [formData.client, formData.year, campaign]);

  const getAvailableInfluencers = (): Influencer[] => {
    const usedNames = new Set(influencerBlocks.map(b => b.influencer_name.toLowerCase()));
    return allInfluencers.filter(inf => !usedNames.has(inf.full_name.toLowerCase()));
  };

  const addInfluencerBlock = () => {
    const newBlock: InfluencerBlockData = {
      id: crypto.randomUUID(),
      influencer_id: null,
      influencer_name: '',
      agreed_fee: 0,
      commission_pct: agencyDefaultCommission ?? AGENCY_DEFAULT_COMMISSION_FALLBACK,
      commission_flat_fee: 0,
      commission_type: 'percentage',
      notes: '',
      deliverables: [],
      existing: false
    };
    setInfluencerBlocks([...influencerBlocks, newBlock]);
    setExpandedBlocks(new Set([newBlock.id]));
  };

  const updateBlock = (id: string, updatedBlock: InfluencerBlockData) => {
    setInfluencerBlocks(blocks =>
      blocks.map(block => block.id === id ? updatedBlock : block)
    );
  };

  const removeBlock = (id: string) => {
    setInfluencerBlocks(blocks => blocks.filter(block => block.id !== id));
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const duplicateBlock = (id: string) => {
    const blockToDuplicate = influencerBlocks.find(b => b.id === id);
    if (!blockToDuplicate) return;

    const newBlock: InfluencerBlockData = {
      id: crypto.randomUUID(),
      influencer_id: null,
      influencer_name: '',
      agreed_fee: blockToDuplicate.agreed_fee,
      commission_pct: blockToDuplicate.commission_pct,
      commission_flat_fee: blockToDuplicate.commission_flat_fee,
      commission_type: blockToDuplicate.commission_type,
      notes: blockToDuplicate.notes,
      deliverables: blockToDuplicate.deliverables.map(d => ({
        id: `temp-${crypto.randomUUID()}`,
        content: d.content,
        date: d.date,
        status: 'Draft' as const,
        fee: d.fee || 0,
        commission: d.commission || 0,
      })),
      existing: false
    };
    setInfluencerBlocks([...influencerBlocks, newBlock]);
    setExpandedBlocks(new Set([newBlock.id]));
  };

  const toggleBlockExpansion = (id: string) => {
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const reorderInfluencerBlocks = (reorderedBlocks: InfluencerBlockData[]) => {
    setInfluencerBlocks(reorderedBlocks);
  };

  const getAvailableServiceProviders = (): ServiceProvider[] => {
    const usedNames = new Set(serviceBlocks.map(b => b.service_provider_name.toLowerCase()));
    return allServiceProviders.filter(sp => !usedNames.has(sp.name.toLowerCase()));
  };

  const addServiceBlock = () => {
    const newBlock: ServiceBlockData = {
      id: crypto.randomUUID(),
      service_provider_id: null,
      service_provider_name: '',
      service_type: 'Other',
      status: 'Requested',
      agreed_fee: 0,
      description: '',
      notes: '',
      existing: false,
      is_recurring: false,
      billing_interval: null,
      custom_interval_days: null,
      recurring_start_date: '',
      recurring_end_date: '',
    };
    setServiceBlocks([...serviceBlocks, newBlock]);
    setExpandedServiceBlocks(new Set([newBlock.id]));
  };

  const updateServiceBlock = (id: string, updatedBlock: ServiceBlockData) => {
    setServiceBlocks(blocks =>
      blocks.map(block => block.id === id ? updatedBlock : block)
    );
  };

  const removeServiceBlock = (id: string) => {
    setServiceBlocks(blocks => blocks.filter(block => block.id !== id));
    setExpandedServiceBlocks(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const duplicateServiceBlock = (id: string) => {
    const blockToDuplicate = serviceBlocks.find(b => b.id === id);
    if (!blockToDuplicate) return;

    const newBlock: ServiceBlockData = {
      id: crypto.randomUUID(),
      service_provider_id: null,
      service_provider_name: '',
      service_type: blockToDuplicate.service_type,
      status: 'Requested',
      agreed_fee: blockToDuplicate.agreed_fee,
      description: blockToDuplicate.description,
      notes: blockToDuplicate.notes,
      existing: false,
      is_recurring: blockToDuplicate.is_recurring,
      billing_interval: blockToDuplicate.billing_interval,
      custom_interval_days: blockToDuplicate.custom_interval_days,
      recurring_start_date: blockToDuplicate.recurring_start_date,
      recurring_end_date: blockToDuplicate.recurring_end_date,
    };
    setServiceBlocks([...serviceBlocks, newBlock]);
    setExpandedServiceBlocks(new Set([newBlock.id]));
  };

  const toggleServiceBlockExpansion = (id: string) => {
    setExpandedServiceBlocks(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const reorderServiceBlocks = (reorderedBlocks: ServiceBlockData[]) => {
    setServiceBlocks(reorderedBlocks);
  };

  const availableInvoiceNames = useMemo(() => {
    const names: Array<{ name: string; type: 'influencer' | 'service'; suggestedAmount: number; sourceId?: string }> = [];

    for (const block of influencerBlocks) {
      if (block.influencer_name) {
        const netFee = calculateInfluencerNetPay(
          block.agreed_fee || 0,
          block.commission_pct ?? 0,
          block.commission_flat_fee ?? 0,
          block.commission_type ?? 'percentage'
        );
        names.push({
          name: block.influencer_name,
          type: 'influencer',
          suggestedAmount: netFee,
          sourceId: block.influencer_id || undefined,
        });
      }
    }

    for (const block of serviceBlocks) {
      if (block.service_provider_name) {
        names.push({
          name: block.service_provider_name,
          type: 'service',
          suggestedAmount: block.agreed_fee || 0,
          sourceId: block.service_provider_id || undefined,
        });
      }
    }

    return names;
  }, [influencerBlocks, serviceBlocks]);

  const influencerCostData = useMemo(() =>
    influencerBlocks.map(b => ({
      agreed_fee: b.agreed_fee || 0,
      commission_pct: b.commission_pct ?? 0,
      commission_flat_fee: b.commission_flat_fee ?? 0,
      commission_type: b.commission_type ?? 'percentage' as const
    })),
    [influencerBlocks]
  );

  const serviceCostData = useMemo(() =>
    serviceBlocks.map(b => ({ agreed_fee: b.agreed_fee || 0 })),
    [serviceBlocks]
  );

  const calculations = useCampaignCalculations(
    formData.campaign_budget || 0,
    influencerCostData,
    serviceCostData
  );

  // Deductions come off what the creators are paid in total; tranches
  // already paid stay as they are.
  const creatorsPayableTotal = Math.max(
    0,
    calculations.totalNetToInfluencers - campaignExpenses
      .filter(e => e.kind !== 'passthrough')
      .reduce((sum, e) => sum + (e.amount || 0), 0)
  );

  // Once the saved payables are loaded, re-split unpaid tranches that no
  // longer add up to what is owed (e.g. a deduction added later). Only when
  // the payables belong to one creator — with several, the split per creator
  // is a decision for the team.
  useEffect(() => {
    if (!savedPayablesLoaded || payablesReconciledRef.current) return;
    payablesReconciledRef.current = true;
    const names = new Set(influencerInvoices.map(inv => inv.source_name).filter(Boolean));
    if (names.size > 1) return;
    const result = reconcilePayables(influencerInvoices, creatorsPayableTotal);
    if (!result) return;
    setInfluencerInvoices(result.invoices);
    setPayableAdjustNote(result.adjustments
      .map(a => `Fatura #${a.index + 1}: ${formatEuro(a.from)} → ${formatEuro(a.to)}`)
      .join(' · '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPayablesLoaded, creatorsPayableTotal]);

  // Paga / Pronta a pagar / Aguarda cliente per payable. Each creator's
  // payables are funded in order by that creator's share of what the client
  // has paid; links to specific client invoices take precedence.
  const payableReadiness = useMemo(() => {
    if (influencerInvoices.length === 0) return undefined;
    const allocations = getCreatorPaymentAllocations(
      { mohit_invoices: clientInvoices, influencer_invoices: influencerInvoices },
      influencerBlocks
        .filter(b => b.influencer_name)
        .map(b => ({
          name: b.influencer_name,
          agreed_fee: b.agreed_fee || 0,
          commission_pct: b.commission_pct ?? 0,
          commission_flat_fee: b.commission_flat_fee ?? 0,
          commission_type: b.commission_type ?? 'percentage',
        }))
    );
    const fundedByCreator = new Map(allocations.map(a => [a.creatorName, a.amountPaid + a.netPayable]));
    return computePayableReadiness(
      influencerInvoices,
      clientInvoices,
      (name) => fundedByCreator.get(name) ?? 0
    );
  }, [influencerInvoices, clientInvoices, influencerBlocks]);

  // Map: clientInvoiceId -> influencerName -> expected net (fee - commission) for linked deliverables
  const expectedNetByClientInvoice = useMemo(() => {
    const result: Record<string, Record<string, number>> = {};
    for (const [invoiceId, delIds] of Object.entries(invoiceDeliverableMap)) {
      if (!delIds || delIds.length === 0) continue;
      const byName: Record<string, number> = {};
      for (const block of influencerBlocks) {
        for (const d of block.deliverables) {
          if (delIds.includes(d.id) && d.fee > 0) {
            const net = d.fee - (d.commission || 0);
            byName[block.influencer_name] = (byName[block.influencer_name] || 0) + net;
          }
        }
      }
      if (Object.keys(byName).length > 0) result[invoiceId] = byName;
    }
    return result;
  }, [invoiceDeliverableMap, influencerBlocks]);

  // Old unlinked supplier invoices get a non-blocking warning badge
  const linkWarningIds = useMemo(() => {
    const linkedPayableIds = new Set(invoiceLinks.map(l => l.payable_invoice_id));
    const warnings = new Set<string>();
    for (const inv of influencerInvoices) {
      if (inv.id.startsWith('temp-')) continue;
      if (initialInfluencerInvoiceIds.has(inv.id) && !linkedPayableIds.has(inv.id)) {
        warnings.add(inv.id);
      }
    }
    return warnings;
  }, [influencerInvoices, invoiceLinks, initialInfluencerInvoiceIds]);

  // ── Stay-open save: keep editing the record this form created ───────────
  const [persistedId, setPersistedId] = useState<string | null>(campaign?.id ?? null);
  const agencyDefaultCommission = useAgencyDefaultCommission();

  // ── Unsaved changes guard ────────────────────────────────────────────────
  const serializeFormState = () => JSON.stringify({
    formData: { ...formData, work_code: undefined },
    influencerBlocks, serviceBlocks, clientInvoices, influencerInvoices, campaignExpenses, invoiceDeliverableMap,
  });
  const dirtySnapshotRef = useRef<string | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  useEffect(() => {
    dirtySnapshotRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotVersion]);
  const isDirty = () => dirtySnapshotRef.current !== null && dirtySnapshotRef.current !== serializeFormState();

  const requestClose = async () => {
    if (isDirty()) {
      const discard = await showConfirm({
        title: t('form.discardChanges.title'),
        message: t('form.discardChanges.message'),
        confirmText: t('common.discard'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!discard) return;
    }
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    let campaignId: string | null = null;
    let isNewCampaign = false;

    try {
      const deliverableIdMap: Record<string, string> = {};
      const clientInvoiceIdMap: Record<string, string> = {};
      const revealGeneral = () => setActiveTab('general');
      const missingRequired = reportFirstMissingField([
        {
          key: 'im-client',
          missing: !formData.client || formData.client.trim() === '',
          message: 'Preenche o Cliente para poder guardar.',
          reveal: revealGeneral,
        },
        {
          key: 'im-work-code',
          missing: (!formData.work_code || formData.work_code.trim() === '') && !!campaign,
          message: 'Preenche o Codigo de Trabalho para poder guardar.',
          reveal: revealGeneral,
        },
      ], toast.error);
      if (missingRequired) {
        setLoading(false);
        return;
      }
      if (influencerBlocks.length === 0 && serviceBlocks.length === 0) {
        throw new ValidationError('At least one influencer or service is required');
      }

      if (influencerBlocks.length > 0) {
        const duplicateNames = influencerBlocks
          .map(b => b.influencer_name.toLowerCase())
          .filter((name, index, arr) => arr.indexOf(name) !== index);

        if (duplicateNames.length > 0) {
          throw new ValidationError(`Duplicate influencer found: ${duplicateNames[0]}`);
        }

        for (const block of influencerBlocks) {
          if (!block.influencer_name || block.influencer_name.trim() === '') {
            throw new ValidationError('All influencer blocks must have a name');
          }
          if (block.agreed_fee <= 0) {
            throw new ValidationError(`Influencer ${block.influencer_name} must have a fee greater than 0`);
          }

          const nonEmptyDeliverables = block.deliverables.filter(d =>
            d.title?.trim() || d.platform?.trim() || d.type?.trim() || d.due_date
          );

          for (const deliverable of nonEmptyDeliverables) {
            if (!deliverable.title || deliverable.title.trim() === '') {
              throw new ValidationError(`All deliverables for ${block.influencer_name} must have a title`);
            }
            if (!deliverable.due_date) {
              throw new ValidationError(`All deliverables for ${block.influencer_name} must have a due date`);
            }
          }
        }
      }

      for (const block of serviceBlocks) {
        if (!block.service_provider_name || block.service_provider_name.trim() === '') {
          throw new ValidationError('All service blocks must have a provider name');
        }
      }

      for (let i = 0; i < influencerInvoices.length; i++) {
        const invoice = influencerInvoices[i];
        if (!invoice.source_name || invoice.source_name.trim() === '') {
          throw new ValidationError(`Fatura ${i + 1} deve ter um nome atribuido`);
        }
      }

      // Block save if new/edited supplier invoices have no linked client invoice
      const linkedPayableIds = new Set(invoiceLinks.map(l => l.payable_invoice_id));
      const hasRealClientInvoices = clientInvoices.some(c => !c.id.startsWith('temp-'));
      if (hasRealClientInvoices) {
        const unlinkedNew = influencerInvoices.filter(inv =>
          !inv.id.startsWith('temp-') &&
          !initialInfluencerInvoiceIds.has(inv.id) &&
          !linkedPayableIds.has(inv.id)
        );
        if (unlinkedNew.length > 0) {
          setActiveTab('influencer-invoices');
          throw new ValidationError('Todas as faturas a pagar devem estar associadas a uma fatura a cobrar');
        }
      }

      const clientErrors = new Set<string>();
      clientInvoices.forEach(inv => {
        if (inv.status === 'Paid' && (!inv.amount_received || inv.amount_received <= 0)) {
          clientErrors.add(inv.id);
        }
      });
      const inflErrors = new Set<string>();
      influencerInvoices.forEach(inv => {
        if (inv.status === 'Paid' && (!inv.amount || inv.amount <= 0)) {
          inflErrors.add(inv.id);
        }
      });

      const paidClientTotal = clientInvoices
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
      // Everything paid but deliverables still open: the campaign would stay
      // Em Andamento / Faturação by mistake, so ask before saving.
      let blocksToSave = influencerBlocks;
      {
        const realDeliverables = influencerBlocks.flatMap(b => b.deliverables.filter(d => d.title?.trim()));
        const clientsSettled = clientInvoices.some(inv => inv.status === 'Paid')
          && clientInvoices.every(inv => inv.status === 'Paid' || inv.status === 'Cancelled');
        const payablesSettled = influencerInvoices.every(inv => inv.status === 'Paid');
        if (realDeliverables.some(d => d.status !== 'Published') && clientsSettled && payablesSettled) {
          const markPublished = await showConfirm({
            title: t('form.unpublishedPaid.title'),
            message: t('form.unpublishedPaid.message'),
            confirmText: t('form.unpublishedPaid.confirm'),
            cancelText: t('form.unpublishedPaid.cancel'),
            variant: 'warning',
          });
          if (markPublished) {
            blocksToSave = influencerBlocks.map(b => ({
              ...b,
              deliverables: b.deliverables.map(d => d.title?.trim() ? { ...d, status: 'Published' as const } : d),
            }));
            setInfluencerBlocks(blocksToSave);
          }
        }
      }

      const allClientPaid = clientInvoices.length > 0 && clientInvoices.every(inv => inv.status === 'Paid');
      // Only meaningful when a budget was actually set; many campaigns derive
      // their value from the creator fees and leave campaign_budget at 0.
      const budget = formData.campaign_budget || 0;
      const clientMismatch = budget > 0 && allClientPaid && Math.abs(paidClientTotal - budget) > 0.01
        ? { expected: budget, actual: paidClientTotal }
        : null;

      const paidInflTotal = influencerInvoices
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);
      const allInflPaid = influencerInvoices.length > 0 && influencerInvoices.every(inv => inv.status === 'Paid');
      const expectedNet = Math.round(creatorsPayableTotal * 100) / 100;
      const inflMismatch = expectedNet > 0 && allInflPaid && Math.abs(paidInflTotal - expectedNet) > 0.01
        ? { expected: expectedNet, actual: paidInflTotal }
        : null;

      setClientInvoiceErrors(clientErrors);
      setInfluencerInvoiceErrors(inflErrors);
      setClientTotalMismatch(clientMismatch);
      setInfluencerTotalMismatch(inflMismatch);

      if (clientErrors.size > 0) {
        throw new ValidationError('Preencha o valor recebido para todas as faturas a receber marcadas como Paga');
      }
      const missingPaymentDate = clientInvoices.filter(inv => inv.status === 'Paid' && !inv.payment_received_date);
      if (missingPaymentDate.length > 0) {
        const nums = missingPaymentDate.map(inv => inv.invoice_number || '(sem número)').join(', ');
        throw new ValidationError(`A fatura nº ${nums} está marcada como Paga mas não tem Data de Recebimento preenchida. Preencha a data para poder guardar.`);
      }
      if (inflErrors.size > 0) {
        throw new ValidationError('Preencha o valor pago para todas as faturas a pagar marcadas como Paga');
      }
      if (clientMismatch) {
        throw new ValidationError(`O total recebido (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(clientMismatch.actual)}) deve ser igual ao valor total da campanha (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(clientMismatch.expected)})`);
      }
      if (inflMismatch) {
        throw new ValidationError(`O total pago (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(inflMismatch.actual)}) deve ser igual ao valor liquido (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(inflMismatch.expected)})`);
      }

      try {
        const existingClient = await clientService.getByName(formData.client);
        if (!existingClient) {
          await clientService.create({
            name: formData.client,
            vat_number: null,
            address: null,
            contact_info: null,
            user_id: user?.id,
          });
        }
      } catch (clientErr) {
        log.warn('Auto-register client failed (likely permissions):', clientErr);
        toast.warning(t('warning.clientAutoRegisterFailed'));
      }

      const serializedExpenses = JSON.stringify(campaignExpenses.filter(e => e.description || e.amount > 0));

      const existingId = persistedId ?? campaign?.id ?? null;
      let savedRecord: CampaignWithComputed | null = null;
      if (existingId) {
        savedRecord = await campaignService.update(existingId, {
          ...formData,
          brand: formData.brand || null,
          campaign_ref: formData.campaign_ref || null,
          agent_id: user?.id || null,
          agent_name: formData.agent_name,
          campaign_mode: 'influencer_marketing',
          campaign_expenses: serializedExpenses,
        });
        campaignId = existingId;
      } else {
        const newCampaign = await campaignService.create({
          ...formData,
          brand: formData.brand || null,
          campaign_ref: formData.campaign_ref || null,
          agent_id: user?.id || null,
          agent_name: formData.agent_name,
          campaign_mode: 'influencer_marketing',
          campaign_expenses: serializedExpenses,
          influencer: '',
          fee: 0,
          commission_pct: agencyDefaultCommission ?? AGENCY_DEFAULT_COMMISSION_FALLBACK,
          deliverables: '[]',
          delivery_date: null,
          delivery_status: 'Draft',
          company_invoice_status: 'Not Issued',
          influencer_invoice_status: 'Not Requested',
          _autoWorkCode: true,
        } as any);
        campaignId = newCampaign.id;
        isNewCampaign = true;
        savedRecord = newCampaign;
        setPersistedId(newCampaign.id);

        try {
          await campaignAccessService.grantAccessToAllUsers(campaignId!);
        } catch (accessError) {
          log.error('Error granting access to users:', accessError);
        }
      }

      const resolvedCampaignId = campaignId!;

      const influencerNameToCiId: Record<string, string> = {};

      for (let i = 0; i < blocksToSave.length; i++) {
        const block = blocksToSave[i];
        let campaignInfluencerId: string;

        if (block.existing) {
          await campaignInfluencerService.update(block.id, {
            influencer_name: block.influencer_name,
            agreed_fee: block.agreed_fee,
            commission_pct: block.commission_pct,
            commission_flat_fee: block.commission_flat_fee,
            commission_type: block.commission_type,
            notes: block.notes || null,
            display_order: i
          });
          campaignInfluencerId = block.id;
        } else {
          const influencer = allInfluencers.find(inf => inf.full_name === block.influencer_name);
          const created = await campaignInfluencerService.create({
            campaign_id: resolvedCampaignId,
            influencer_id: influencer?.id || null,
            influencer_name: block.influencer_name,
            status: 'Accepted',
            agreed_fee: block.agreed_fee,
            currency: 'EUR',
            commission_pct: block.commission_pct,
            commission_flat_fee: block.commission_flat_fee,
            commission_type: block.commission_type,
            usage_rights_json: null,
            agreement_url: null,
            notes: block.notes || null,
            display_order: i
          });
          campaignInfluencerId = created.id;
        }

        if (block.influencer_name) {
          influencerNameToCiId[block.influencer_name] = campaignInfluencerId;
        }

        for (const deliverable of block.deliverables) {
          const deliverableData = {
            campaign_id: resolvedCampaignId,
            campaign_influencer_id: campaignInfluencerId,
            title: deliverable.content,
            platform: null,
            type: null,
            due_date: deliverable.date || null,
            status: deliverable.status,
            fee: deliverable.fee || null,
            commission: deliverable.commission || 0,
            proof_url: null,
            published_at: null,
            published_url: null,
            description: null
          };

          if (block.existing && deliverable.id && !deliverable.id.includes('temp-')) {
            await deliverableService.update(deliverable.id, deliverableData);
            deliverableIdMap[deliverable.id] = deliverable.id;
          } else {
            const created = await deliverableService.create(deliverableData);
            deliverableIdMap[deliverable.id] = created.id;
          }
        }
      }

      for (let j = 0; j < serviceBlocks.length; j++) {
        const block = serviceBlocks[j];
        const recurringFields = {
          is_recurring: block.is_recurring,
          billing_interval: block.is_recurring ? block.billing_interval : null,
          custom_interval_days: block.is_recurring && block.billing_interval === 'custom' ? block.custom_interval_days : null,
          recurring_start_date: block.is_recurring && block.recurring_start_date ? block.recurring_start_date : null,
          recurring_end_date: block.is_recurring && block.recurring_end_date ? block.recurring_end_date : null,
        };

        if (block.existing) {
          await campaignServiceService.update(block.id, {
            service_provider_name: block.service_provider_name,
            service_type: block.service_type,
            status: block.status,
            agreed_fee: block.agreed_fee,
            description: block.description || null,
            notes: block.notes || null,
            display_order: j,
            ...recurringFields,
          });
        } else {
          try {
            const existingProvider = await serviceProviderService.getByName(block.service_provider_name);
            if (!existingProvider && block.service_provider_name) {
              await serviceProviderService.create({
                name: block.service_provider_name,
                type: block.service_type,
                contact_name: null,
                email: null,
                phone: null,
                address: null,
                vat_number: null,
                notes: null
              });
            }
          } catch (providerErr) {
            log.warn('Auto-register service provider failed:', providerErr);
          }

          const provider = allServiceProviders.find(sp => sp.name === block.service_provider_name);
          await campaignServiceService.create({
            campaign_id: resolvedCampaignId,
            service_provider_id: provider?.id || null,
            service_provider_name: block.service_provider_name,
            service_type: block.service_type,
            status: block.status,
            agreed_fee: block.agreed_fee,
            currency: 'EUR',
            description: block.description || null,
            notes: block.notes || null,
            display_order: j,
            ...recurringFields,
          });
        }
      }

      // Persist the payment date of a Paid invoice as a payment record —
      // it lives in the `payments` table, not on the invoice row, and is
      // what payment_received_date is loaded from when the form reopens.
      // Invoices with multiple payments or tranche payments are managed by
      // the tranche system and are left untouched.
      const syncInvoicePayment = async (invoiceId: string, isPaid: boolean, paidDate: string, amount: number) => {
        if (!isPaid || !paidDate || !invoiceId || invoiceId.startsWith('temp-')) return;
        const payments = await invoiceService.getPaymentsByInvoice(invoiceId);
        if (payments.length === 0) {
          await invoiceService.recordPayment({
            invoice_id: invoiceId,
            amount,
            paid_at: paidDate,
            method: null,
            reference: null,
            notes: null,
          });
        } else if (payments.length === 1 && !(payments[0] as { tranche_id?: string }).tranche_id) {
          const existing = payments[0];
          const existingDate = (existing.paid_at || '').slice(0, 10);
          if (existingDate !== paidDate.slice(0, 10) || existing.amount !== amount) {
            await invoiceService.updatePayment(existing.id, { paid_at: paidDate, amount });
          }
        }
      };

      for (let i = 0; i < clientInvoices.length; i++) {
        const invoice = clientInvoices[i];
        const amount = invoice.amount_received || 0;
        const invoiceData = {
          campaign_id: resolvedCampaignId,
          campaign_influencer_id: null,
          type: 'Client' as const,
          number: invoice.invoice_number || null,
          issue_date: invoice.issued_date || null,
          due_date: invoice.due_date || null,
          ...netToInvoiceAmounts(amount),
          currency: 'EUR',
          status: invoice.status === 'Not Issued' ? 'Draft' as const : invoice.status === 'Paid' ? 'Paid' as const : invoice.status === 'Cancelled' ? 'Canceled' as const : 'Issued' as const,
          pdf_url: null,
          counterparty_name: formData.client,
          counterparty_email: null,
          notes: null,
          display_order: i,
        };

        const isExisting = invoice.id && !invoice.id.startsWith('temp-') && invoice.id.length === 36 && invoice.id.includes('-');

        if (isExisting && campaign) {
          const existingInvoice = await invoiceService.getById(invoice.id);
          if (existingInvoice) {
            await invoiceService.update(invoice.id, invoiceData);
            clientInvoiceIdMap[invoice.id] = invoice.id;
          } else {
            const created = await invoiceService.create(invoiceData);
            if (created) clientInvoiceIdMap[invoice.id] = created.id;
          }
        } else {
          const created = await invoiceService.create(invoiceData);
          if (created) clientInvoiceIdMap[invoice.id] = created.id;
        }

        await syncInvoicePayment(
          clientInvoiceIdMap[invoice.id] || '',
          invoice.status === 'Paid',
          invoice.payment_received_date,
          amount
        );
      }

      for (let i = 0; i < influencerInvoices.length; i++) {
        const invoice = influencerInvoices[i];
        const resolvedCiId = invoice.source_name ? (influencerNameToCiId[invoice.source_name] || null) : null;
        const invoiceData = {
          campaign_id: resolvedCampaignId,
          campaign_influencer_id: resolvedCiId,
          type: 'Influencer' as const,
          number: invoice.invoice_number || null,
          issue_date: invoice.issue_date || null,
          due_date: invoice.due_date || null,
          ...netToInvoiceAmounts(invoice.amount || 0),
          currency: 'EUR',
          // 'Requested' has no invoices-table equivalent: store as Draft with a
          // notes marker so it round-trips instead of degrading to Issued/Received.
          status: invoice.status === 'Not Requested' || invoice.status === 'Requested'
            ? 'Draft' as const
            : invoice.status === 'Paid' ? 'Paid' as const : 'Issued' as const,
          pdf_url: null,
          counterparty_name: invoice.source_name || null,
          counterparty_email: null,
          notes: invoice.status === 'Requested' ? 'STATUS: Requested' : null,
          display_order: i,
        };

        const isExisting = invoice.id && !invoice.id.startsWith('temp-') && invoice.id.length === 36 && invoice.id.includes('-');

        let savedInvoiceId = '';
        if (isExisting && campaign) {
          const existingInvoice = await invoiceService.getById(invoice.id);
          if (existingInvoice) {
            await invoiceService.update(invoice.id, invoiceData);
            savedInvoiceId = invoice.id;
          } else {
            const created = await invoiceService.create(invoiceData);
            if (created) savedInvoiceId = created.id;
          }
        } else {
          const created = await invoiceService.create(invoiceData);
          if (created) savedInvoiceId = created.id;
        }

        await syncInvoicePayment(
          savedInvoiceId,
          invoice.status === 'Paid',
          invoice.payment_received_date,
          invoice.amount || 0
        );
      }

      // Sync invoice-deliverable links
      const feeByDeliverableId: Record<string, number> = {};
      blocksToSave.forEach(b => b.deliverables.forEach(d => {
        feeByDeliverableId[d.id] = d.fee || 0;
      }));

      for (const [formInvoiceId, formDeliverableIds] of Object.entries(invoiceDeliverableMap)) {
        const realInvoiceId = clientInvoiceIdMap[formInvoiceId] || formInvoiceId;
        if (realInvoiceId.startsWith('temp-')) continue;
        const items = formDeliverableIds
          .map(fid => ({
            deliverableId: deliverableIdMap[fid] || fid,
            fee: feeByDeliverableId[fid] || 0,
          }))
          .filter(it => it.deliverableId && !it.deliverableId.startsWith('temp-'));
        try {
          await deliverableService.syncInvoiceLinks(realInvoiceId, items);
        } catch (e) {
          log.warn('Failed to sync invoice deliverable links for', realInvoiceId, e);
        }
      }

      toast.success(existingId ? t('form.campaign.updateSuccess') : t('form.campaign.createSuccess'));
      // Stay open; the page refreshes the list and hands the record back
      onSave(savedRecord ?? undefined);
    } catch (err: unknown) {
      if (isNewCampaign && campaignId) {
        try {
          await campaignService.delete(campaignId);
        } catch (rollbackErr) {
          log.error('Failed to rollback orphaned campaign:', rollbackErr);
        }
      }
      const technicalMessage = err instanceof Error
        ? err.message
        : (err as { message?: string })?.message || 'Unknown error';
      const userMessage = translateError(err);
      setError({ message: userMessage, technical: technicalMessage });
    } finally {
      setLoading(false);
    }
  };

  const persistInvoiceOrder = useCallback(async (invoices: { id: string }[], type: 'Client' | 'Influencer') => {
    if (!campaign?.id) return;
    const existingIds = invoices
      .filter(inv => inv.id && !inv.id.startsWith('temp-') && inv.id.length === 36 && inv.id.includes('-'))
      .map(inv => inv.id);
    if (existingIds.length < 2) return;
    try {
      await invoiceService.reorderInvoices(campaign.id, type, existingIds);
    } catch {
      // Order will be saved on next form submit
    }
  }, [campaign?.id]);

  const handleClientInvoicesChange = useCallback((invoices: MohitInvoice[]) => {
    setClientInvoices(invoices);
    setClientInvoiceErrors(new Set());
    setClientTotalMismatch(null);
    persistInvoiceOrder(invoices, 'Client');
  }, [persistInvoiceOrder]);

  const handleCreateLink = useCallback(async (payableId: string, clientId: string) => {
    if (clientId.startsWith('temp-') || payableId.startsWith('temp-')) {
      toast.warning('Guarda a campanha antes de criar correlacoes entre faturas.');
      return;
    }
    try {
      const link = await invoiceLinkService.create(clientId, payableId);
      setInvoiceLinks(prev => [...prev.filter(l => l.payable_invoice_id !== payableId), link]);

      // Pre-fill supplier invoice amount with expected net if currently zero
      const inv = influencerInvoices.find(i => i.id === payableId);
      if (inv && inv.amount === 0 && inv.source_name) {
        const netMap = expectedNetByClientInvoice[clientId];
        const expectedAmount = netMap?.[inv.source_name];
        if (expectedAmount && expectedAmount > 0) {
          setInfluencerInvoices(prev =>
            prev.map(i => i.id === payableId ? { ...i, amount: Math.round(expectedAmount * 100) / 100 } : i)
          );
        }
      }
    } catch (err) {
      log.error('Failed to create invoice link:', err);
      toast.error('Falha ao criar correlacao entre faturas.');
    }
  }, [toast, influencerInvoices, expectedNetByClientInvoice]);

  const handleRemoveLink = useCallback(async (payableId: string) => {
    const existing = invoiceLinks.find(l => l.payable_invoice_id === payableId);
    if (!existing) return;
    try {
      await invoiceLinkService.deleteByPair(existing.client_invoice_id, payableId);
      setInvoiceLinks(prev => prev.filter(l => l.payable_invoice_id !== payableId));
    } catch (err) {
      log.error('Failed to remove invoice link:', err);
      toast.error('Falha ao remover correlacao.');
    }
  }, [toast, invoiceLinks]);

  const handleInfluencerInvoicesChange = useCallback((invoices: InfluencerInvoice[]) => {
    setInfluencerInvoices(invoices);
    setInfluencerInvoiceErrors(new Set());
    setInfluencerTotalMismatch(null);
    persistInvoiceOrder(invoices, 'Influencer');
  }, [persistInvoiceOrder]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    handleFieldChange(name, type === 'number' ? parseFloat(value) || 0 : value);
  };

  const tabs: TabItem[] = [
    {
      id: 'general',
      label: 'Geral',
      icon: FileText,
      badge: undefined,
      hasError: false,
    },
    {
      id: 'content',
      label: 'Conteúdos',
      icon: Package,
      badge: influencerBlocks.length + serviceBlocks.length,
      hasError: false,
    },
    {
      id: 'agency-invoices',
      label: 'Faturas a Cobrar',
      icon: Receipt,
      badge: clientInvoices.length,
      hasError: false,
    },
    {
      id: 'influencer-invoices',
      label: 'Faturas a Pagar',
      icon: UserIcon,
      badge: influencerInvoices.length,
      hasError: linkWarningIds.size > 0,
    },
  ];

  return (
    <div className="cm-shell cm-shell--fullpage fixed inset-0 z-50 flex flex-col bg-white">

        {/* cm-header */}
        <div className="cm-header relative bg-white border-b border-gray-200 px-4 sm:px-8 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <h2 className="cm-title text-xl sm:text-2xl font-semibold text-gray-900 truncate">
                {campaign ? t('form.campaign.editTitle') : 'Nova Campanha'}
              </h2>
              <span className="cm-chip cm-chip-neutral hidden sm:inline-flex">Multi-influencer</span>
            </div>
            {isAdmin && (
              <div className="mt-1.5">
                <AdminOnlyToggle
                  value={!!formData.admin_only}
                  onChange={(next) => setFormData(prev => ({ ...prev, admin_only: next }))}
                />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          {/* cm-tabbar */}
          <nav className="cm-tabbar flex gap-1 px-4 sm:px-8" role="tablist">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`tabpanel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`cm-tab flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors relative ${
                    isActive
                      ? 'cm-tab--active bg-gray-50 text-gray-900 border-b-2 border-accent-600'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className={`cm-tab-badge ml-0.5 px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                      isActive ? 'bg-accent-100 text-accent-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                  {tab.hasError && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-gray-50">
          <div className="cm-body flex-1 overflow-y-auto">
            {error && (
              <ErrorMessage
                message={error.message}
                technicalError={error.technical}
                className="m-4 sm:m-8"
                onDismiss={() => setError(null)}
              />
            )}

            {activeTab === 'general' && (
              <GeneralInfoTab
                formData={formData}
                clients={clients}
                isEditing={!!campaign}
                agentName={agentName}
                calculations={calculations}
                influencersCount={influencerBlocks.length}
                onChange={handleFieldChange}
                onClientCreated={(name) => {
                  setClients(prev => [...prev, name].sort((a, b) => a.localeCompare(b)));
                }}
              />
            )}

            {activeTab === 'content' && (
              <ContentTab
                influencerBlocks={influencerBlocks}
                serviceBlocks={serviceBlocks}
                availableInfluencers={getAvailableInfluencers()}
                availableServiceProviders={getAvailableServiceProviders()}
                expandedInfluencers={expandedBlocks}
                expandedServices={expandedServiceBlocks}
                onAddInfluencer={addInfluencerBlock}
                onAddService={addServiceBlock}
                onToggleInfluencer={toggleBlockExpansion}
                onToggleService={toggleServiceBlockExpansion}
                onInfluencerChange={updateBlock}
                onServiceChange={updateServiceBlock}
                onRemoveInfluencer={removeBlock}
                onRemoveService={removeServiceBlock}
                onDuplicateInfluencer={duplicateBlock}
                onDuplicateService={duplicateServiceBlock}
                onReorderInfluencers={reorderInfluencerBlocks}
                onReorderServices={reorderServiceBlocks}
              />
            )}

            {activeTab === 'agency-invoices' && (
              <div className="m-4 sm:m-8 mt-6 sm:mt-8">
                <MohitInvoices
                  invoices={clientInvoices}
                  onChange={handleClientInvoicesChange}
                  totalCommission={calculations.totalInfluencerCommissions}
                  requests={{
                    campaignId: persistedId,
                    campaignName: [formData.work_code, formData.brand].filter(Boolean).join(' · ') || 'Campanha',
                    purchaseOrder: clientInvoices.find(inv => inv.purchase_order)?.purchase_order,
                  }}
                  clientDetails={clientDetails}
                  totalAmount={formData.campaign_budget}
                  errorIds={clientInvoiceErrors}
                  totalMismatch={clientTotalMismatch}
                  influencerCommissions={influencerBlocks.map(b => ({
                    influencer_name: b.influencer_name,
                    agreed_fee: b.agreed_fee,
                    commission_pct: b.commission_pct,
                    commission_flat_fee: b.commission_flat_fee,
                    commission_type: b.commission_type,
                  } as InvoiceInfluencerCommission))}
                  invoiceLinks={invoiceLinks}
                  payableInvoices={influencerInvoices}
                  allDeliverables={influencerBlocks.flatMap(b =>
                    b.deliverables.filter(d => d.fee > 0).map(d => {
                      const claimingInvoiceId = Object.entries(invoiceDeliverableMap).find(
                        ([, ids]) => ids.includes(d.id)
                      )?.[0];
                      const claimingInvoice = claimingInvoiceId
                        ? clientInvoices.find(inv => inv.id === claimingInvoiceId)
                        : undefined;
                      return {
                        id: d.id,
                        content: d.content,
                        fee: d.fee,
                        commission: d.commission || 0,
                        influencer_name: b.influencer_name,
                        status: d.status,
                        lockedByInvoiceId: claimingInvoiceId,
                        lockedByInvoiceNumber: claimingInvoice?.invoice_number,
                      } as DeliverableItem;
                    })
                  )}
                  selectedDeliverables={invoiceDeliverableMap}
                  onDeliverablesChange={(invoiceId, ids) => {
                    setInvoiceDeliverableMap(prev => ({ ...prev, [invoiceId]: ids }));
                    const allDels = influencerBlocks.flatMap(b =>
                      b.deliverables.filter(d => d.fee > 0).map(d => ({
                        id: d.id, fee: d.fee, commission: d.commission || 0,
                      }))
                    );
                    const total = allDels
                      .filter(d => ids.includes(d.id))
                      .reduce((s, d) => s + d.fee, 0);
                    const updatedInvoices = clientInvoices.map(inv =>
                      inv.id === invoiceId ? { ...inv, amount_received: Math.round(total * 100) / 100 } : inv
                    );
                    handleClientInvoicesChange(updatedInvoices);
                  }}
                />
              </div>
            )}

            {activeTab === 'influencer-invoices' && (
              <div className="m-4 sm:m-8 mt-6 sm:mt-8 space-y-6">
                {payableAdjustNote && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-xs text-amber-800">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Faturas a pagar atualizadas</p>
                      <p>Os criadores recebem {formatEuro(creatorsPayableTotal)} (já descontadas as despesas). As faturas por pagar foram ajustadas para bater certo — carrega em Guardar para aplicar.</p>
                      <p className="font-medium mt-0.5">{payableAdjustNote}</p>
                    </div>
                  </div>
                )}
                {!clientInvoices.some(c => !c.id.startsWith('temp-')) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                    <p className="text-xs text-amber-800">
                      Cria primeiro uma fatura a cobrar (separador Faturas a Cobrar) para poderes associar as faturas a pagar.
                    </p>
                  </div>
                )}
                <InfluencerInvoices
                  invoices={influencerInvoices}
                  onChange={handleInfluencerInvoicesChange}
                  availableNames={availableInvoiceNames}
                  netFeeInfluencer={creatorsPayableTotal}
                  readiness={payableReadiness}
                  errorIds={influencerInvoiceErrors}
                  totalMismatch={influencerTotalMismatch}
                  clientInvoices={clientInvoices}
                  invoiceLinks={invoiceLinks}
                  onCreateLink={handleCreateLink}
                  onRemoveLink={handleRemoveLink}
                  requireLink={true}
                  linkWarningIds={linkWarningIds}
                  expectedNetByClientInvoice={expectedNetByClientInvoice}
                  totalCommission={calculations.totalInfluencerCommissions}
                />
                <CampaignExpenses expenses={campaignExpenses} onChange={setCampaignExpenses} />
              </div>
            )}
          </div>

          {/* cm-footer */}
          <div className="cm-footer flex justify-end gap-3 px-4 sm:px-8 py-3 sm:py-4 border-t border-gray-200 bg-white">
            <Button
              type="button"
              onClick={requestClose}
              variant="secondary"
              size="md"
              className="cm-btn cm-btn-secondary"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              size="md"
              className="cm-btn cm-btn-primary"
            >
              {loading ? 'A guardar...' : campaign ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
    </div>
  );
}
